<template>
    <div class="customer-v3-container">
        <v-app class="ppv2-bg-white dark:ppv2-bg-dark ppv2-rounded-3xl ppv2-p-6">
            <!-- Header -->
            <Header :title="$t('profil.title')" />

            <!-- Purple Header Card -->
            <div
                class="ppv2-w-full ppv2-rounded-t-[10px] ppv2-bg-[#7D2DBD] ppv2-bg-opacity-10 ppv2-p-6 ppv2-flex ppv2-items-center"
                :style="{ gap: normalizeSize(16, true) }"
            >
                <!-- Avatar -->
                <div
                    class="ppv2-h-[64px] ppv2-w-[64px] ppv2-rounded-full ppv2-bg-[#2A1557] ppv2-flex ppv2-items-center ppv2-justify-center ppv2-text-white ppv2-text-xl poppinsMedium"
                >
                    {{ userInitials }}
                </div>
                <!-- Name & Role -->
                <div class="ppv2-flex ppv2-flex-col">
                    <span class="poppinsBold ppv2-text-lg"> {{ form.first_name }} {{ form.last_name }} </span>
                    <span class="ppv2-text-black ppv2-text-opacity-70 ppv2-text-sm">
                        {{ isAdmin ? $t('profil.admin') : $t('profil.user') }}
                    </span>
                </div>
            </div>

            <!-- Main Info Card -->
            <div class="ppv2-w-full ppv2-rounded-b-[10px] ppv2-border ppv2-border-t-0 ppv2-border-[#EAECF0] ppv2-p-8 ppv2-mb-6">
                <!-- Top Row: Edit Button -->
                <div class="ppv2-flex ppv2-justify-end ppv2-mb-6">
                    <BaseButton
                        @click="showProfileModal = true"
                        :customFontSize="14"
                        customClass="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-px-4 ppv2-py-2 ppv2-border ppv2-border-[#344054] !ppv2-rounded-full !ppv2-text-[#344054] poppinsMedium hover:ppv2-bg-gray-50 transition !ppv2-bg-transparent !ppv2-h-auto"
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M11.3333 2.00004C11.6869 1.64644 12.1664 1.44775 12.6666 1.44775C13.1668 1.44775 13.6463 1.64644 14 2.00004C14.3536 2.35364 14.5522 2.83315 14.5522 3.33337C14.5522 3.83359 14.3536 4.3131 14 4.66671L5.00004 13.6667L1.33337 14.6667L2.33337 11L11.3333 2.00004Z"
                                stroke="#344054"
                                stroke-width="1.33333"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                        <span class="ppv2-text-black">{{ $t('profil.edit') }}</span>
                    </BaseButton>
                </div>

                <!-- Info Grid -->
                <div class="ppv2-grid ppv2-grid-cols-1 md:ppv2-grid-cols-2 ppv2-gap-y-8 ppv2-gap-x-12">
                    <!-- Nom -->
                    <div class="ppv2-flex ppv2-flex-col">
                        <span class="ppv2-text-sm ppv2-text-black ppv2-text-opacity-70 ppv2-mb-1">{{
                            $t('profil.last_name.label')
                        }}</span>
                        <span class="ppv2-text-base poppinsMedium">{{ form.last_name }}</span>
                    </div>
                    <!-- Prénom -->
                    <div class="ppv2-flex ppv2-flex-col">
                        <span class="ppv2-text-sm ppv2-text-black ppv2-text-opacity-70 ppv2-mb-1">{{
                            $t('profil.first_name.label')
                        }}</span>
                        <span class="ppv2-text-base poppinsMedium">{{ form.first_name }}</span>
                    </div>
                    <!-- Email -->
                    <div class="ppv2-flex ppv2-flex-col">
                        <span class="ppv2-text-sm ppv2-text-black ppv2-text-opacity-70 ppv2-mb-1">{{
                            $t('profil.email.label')
                        }}</span>
                        <span class="ppv2-text-base poppinsMedium">{{ form.email }}</span>
                    </div>
                    <!-- Phone -->
                    <div class="ppv2-flex ppv2-flex-col">
                        <span class="ppv2-text-sm ppv2-text-black ppv2-text-opacity-70 ppv2-mb-1">{{
                            $t('profil.phone_number.label')
                        }}</span>
                        <span class="ppv2-text-base poppinsMedium">{{ form.phone_number }}</span>
                    </div>
                </div>
            </div>

            <!-- Profile Edit Modal -->
            <ProfileEditModal v-model="showProfileModal" :initialData="form" :isSaving="isSaving" :errors="errors" @save="save" />

            <!-- Password Card -->
            <div
                class="ppv2-w-full ppv2-rounded-[10px] ppv2-border ppv2-border-[#EAECF0] ppv2-p-6 ppv2-flex ppv2-justify-between ppv2-items-center"
            >
                <span class="poppinsBold ppv2-text-sm">{{ $t('profil.change_password') }}</span>
                <BaseButton
                    @click="showPasswordModal = true"
                    :customFontSize="14"
                    customClass="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-px-4 ppv2-py-2 ppv2-border ppv2-border-[#344054] !ppv2-rounded-full !ppv2-text-[#344054] poppinsMedium hover:ppv2-bg-gray-50 transition !ppv2-bg-transparent !ppv2-h-auto"
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M11.3333 2.00004C11.6869 1.64644 12.1664 1.44775 12.6666 1.44775C13.1668 1.44775 13.6463 1.64644 14 2.00004C14.3536 2.35364 14.5522 2.83315 14.5522 3.33337C14.5522 3.83359 14.3536 4.3131 14 4.66671L5.00004 13.6667L1.33337 14.6667L2.33337 11L11.3333 2.00004Z"
                            stroke="#344054"
                            stroke-width="1.33333"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                    <span class="ppv2-text-black">{{ $t('profil.edit') }}</span>
                </BaseButton>
            </div>

            <!-- Password Modal -->
            <PasswordModal
                v-model="showPasswordModal"
                :isSaving="isSavingPassword"
                :errors="passwordErrors"
                @save="updatePassword"
            />
        </v-app>
    </div>
</template>

<script>
import axios from 'axios'
import normalizeSize from '../../../utils/normalizeSize'
import CustomInput from '../../Inputs/CustomInput.vue'
import BackButton from '../../Shared/BackButton.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import BaseSwitch from '../../Shared/BaseSwitch.vue'
import PasswordModal from './PasswordModal.vue'
import ProfileEditModal from './ProfileEditModal.vue'
import Header from '../../Shared/Header.vue'

export default {
    props: {
        user: {
            type: Object,
            required: true,
        },
        token: {
            type: String,
            default: '',
        },
    },
    components: {
        CustomInput,
        BackButton,
        BaseButton,
        BaseSwitch,
        PasswordModal,
        ProfileEditModal,
        Header,
    },
    data() {
        return {
            form: {
                id: '',
                first_name: '',
                last_name: '',
                email: '',
                phone_number: '',
                canBeClient: false,
            },
            isSaving: false,
            errors: {},
            showPasswordModal: false,
            isSavingPassword: false,
            passwordErrors: {},
            showProfileModal: false,
        }
    },
    computed: {
        isAdmin() {
            return this.user.roles?.includes('Administrateur')
        },
        userInitials() {
            const first = this.form.first_name ? this.form.first_name.charAt(0).toUpperCase() : ''
            const last = this.form.last_name ? this.form.last_name.charAt(0).toUpperCase() : ''
            return `${first}${last}`
        },
    },
    methods: {
        normalizeSize,
        async save(modalData) {
            // Validate modal data locally if valid simple checks needed,
            // otherwise trust backend validation or add specific validators.
            this.errors = {}
            if (!modalData.last_name) {
                this.errors.last_name = this.$t('form_customer_v2.main_information.last_name.required')
            }
            if (!modalData.first_name) {
                this.errors.first_name = this.$t('form_customer_v2.main_information.first_name.required')
            }
            if (!modalData.email) {
                this.errors.email = this.$t('form_customer_v2.main_information.email.required')
            }
            if (Object.keys(this.errors).length > 0) {
                this.$toast.error(this.$t('common.formValidationError'))
                return
            }

            this.isSaving = true
            try {
                // Merge modal data into form for submission
                const payload = { ...this.form, ...modalData }

                await axios.put('/api/v3/profil/put', payload, {
                    headers: { Authorization: `Bearer ${this.token}` },
                })

                // Update local form with new data on success
                this.form = { ...this.form, ...modalData }

                this.$toast.success(this.$t('common.success'))
                this.showProfileModal = false
            } catch (error) {
                this.$toast.error(error.response.data.message)
                if (error.response && error.response.status === 422) {
                    this.errors = error.response.data.errors || {}
                }
            } finally {
                this.isSaving = false
            }
        },
        async updatePassword(passwordData) {
            this.isSavingPassword = true
            this.passwordErrors = {}

            try {
                const payload = {
                    ...passwordData,
                    user_id: this.form.id,
                }
                await axios.put('/api/v3/profil/password', payload, {
                    headers: { Authorization: `Bearer ${this.token}` },
                })
                this.$toast.success(this.$t('common.success'))
                this.showPasswordModal = false
            } catch (error) {
                this.$toast.error(error.response.data.message)
                if (error.response && error.response.status === 422) {
                    this.passwordErrors = error.response.data.errors || {}
                    // Check if message is a string for generic error
                    if (error.response.data.message && typeof error.response.data.message === 'string') {
                        this.$toast.error(error.response.data.message)
                    }
                }
            } finally {
                this.isSavingPassword = false
            }
        },
        cancel() {
            this.exit()
        },
        exit() {
            window.location.href = '/back-office/v3/customer'
        },
    },
    watch: {
        user: {
            immediate: true,
            handler(user) {
                if (!user) return

                this.form = {
                    id: user.id,
                    first_name: user.first_name || '',
                    last_name: user.last_name || '',
                    email: user.email || '',
                    phone_number: user.phone_number || '', // Added binding for phone number
                    canBeClient: user.roles?.includes('client') || false,
                }
            },
        },
    },
}
</script>
<style scoped lang="scss">
.customer-v3-container {
    padding: 0;
}

:deep(.v-application--wrap) {
    min-height: auto;
}
#main-container.customer-v3-no-bg {
    background: transparent !important;
    border-radius: 0px;
}
</style>
